import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import TableCell from "@mui/material/TableCell";

import TableSortLabel from "components/DesignComponents/TableSortLabel";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import { HeadTitleInfo } from "components/Table/components/HeadTitleInfo";
import { useSorting } from "components/Table/hooks/useSorting";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import { useOverflow } from "shared/hooks/useOverflow";
import { tableColumnRightDropShadow } from "shared/styles/styleFunctions";
import { ORDERS } from "shared/types/enums";

import { HeadColumnProps } from "./types";

const HeadColumn = ({ data, scrollHorizontal }: HeadColumnProps) => {
    const { queryParams, setQueryParams } = useContext(ParamsContext);

    const {
        isInnerTable,
        isNotSelectable,
        innerTableQueryParams,
        fixedColumnWidth,
        setInnerTableParams
    } = useContext(TableContext);

    const { t } = useTranslation();
    const { ref, isOverflowing } = useOverflow();

    const { order, sort, sortColumn } = useSorting(
        isInnerTable ? innerTableQueryParams : queryParams,
        isInnerTable ? setInnerTableParams : setQueryParams
    );

    const {
        name,
        columnIdentifier,
        sortResource,
        isSticky,
        restriction,
        isNameNotTranslatable
    } = data;

    const isActiveSorting = sort === sortResource;
    const sortDirection = isActiveSorting ? order : ORDERS.Descending;

    const finalName = isNameNotTranslatable ? name : t(`Table##${name}`);

    const setLeftPosition = () => {
        if (isSticky) {
            return isNotSelectable ? "0" : "60px";
        }

        return "unset";
    };

    return (
        <TableCell
            key={name}
            css={css([
                {
                    left: setLeftPosition(),
                    zIndex: isSticky ? "3" : "2",

                    "& .MuiTableSortLabel-icon": {
                        fontSize: "16px",
                        marginRight: "0",
                        opacity: "inherit !important"
                    }
                },
                isSticky && tableColumnRightDropShadow(scrollHorizontal)
            ])}
        >
            {sortResource ? (
                <TableSortLabel
                    isActive={isActiveSorting}
                    direction={sortDirection}
                    onClick={() => sortColumn(sortResource)}
                >
                    <HeadTitleInfo
                        restriction={restriction}
                        columnWithAdditionalInfo={columnIdentifier}
                        css={css({
                            marginRight: "4px"
                        })}
                    />

                    {finalName}
                </TableSortLabel>
            ) : (
                <TooltipGeneral
                    title={finalName}
                    disableHoverListener={!isOverflowing}
                    placement={TOOLTIP_PLACEMENT.Top}
                >
                    <span
                        ref={ref}
                        css={css({
                            display: fixedColumnWidth ? "block" : "flex",
                            alignItems: "center",
                            overflowX: "hidden",
                            textOverflow: "ellipsis"
                        })}
                    >
                        <HeadTitleInfo
                            restriction={restriction}
                            columnWithAdditionalInfo={columnIdentifier}
                            css={css({
                                marginRight: "4px"
                            })}
                        />

                        {finalName}
                    </span>
                </TooltipGeneral>
            )}
        </TableCell>
    );
};

export default HeadColumn;
